import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';

const CameraContext = createContext();

export const useCamera = () => {
  const context = useContext(CameraContext);
  if (!context) {
    throw new Error('useCamera must be used within CameraProvider');
  }
  return context;
};

// Permission status constants
const PERMISSION_STATUS = {
  UNKNOWN: 'unknown',
  GRANTED: 'granted',
  DENIED: 'denied',
  PROMPT: 'prompt'
};

const CAMERA_PERMISSION_KEY = 'quickcheck_camera_permission';

// Session-level tracking (survives component unmounts but not page refresh)
let sessionStreamInitialized = false;
let sessionStreamId = null;

export const CameraProvider = ({ children }) => {
  const [permissionStatus, setPermissionStatus] = useState(() => {
    const stored = localStorage.getItem(CAMERA_PERMISSION_KEY);
    console.log('[CameraContext] Initial permission from storage:', stored);
    return stored || PERMISSION_STATUS.UNKNOWN;
  });
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [isCameraVisible, setIsCameraVisible] = useState(false);
  
  // Refs persist across renders and route changes
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const initializingRef = useRef(false);

  // Check if we have a valid active stream
  const hasActiveStream = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return false;
    
    // Check if stream is still active
    const tracks = stream.getTracks();
    const hasActiveTracks = tracks.length > 0 && tracks.some(t => t.readyState === 'live');
    
    console.log('[CameraContext] hasActiveStream check:', {
      hasStream: !!stream,
      trackCount: tracks.length,
      hasActiveTracks,
      sessionStreamId
    });
    
    return hasActiveTracks;
  }, []);

  // Initialize stream once and keep it alive - only call getUserMedia when truly needed
  const initializeStream = useCallback(async (forceNew = false) => {
    console.log('[CameraContext] initializeStream called', {
      forceNew,
      initializingRef: initializingRef.current,
      sessionStreamInitialized,
      sessionStreamId
    });

    // Prevent multiple simultaneous initializations
    if (initializingRef.current) {
      console.log('[CameraContext] Already initializing, skipping duplicate call');
      return streamRef.current;
    }

    // Check for existing active stream - CRITICAL for iOS
    if (!forceNew && hasActiveStream()) {
      console.log('[CameraContext] REUSING existing active stream - NO getUserMedia call');
      setIsStreamReady(true);
      return streamRef.current;
    }

    // Check if permission was denied
    if (permissionStatus === PERMISSION_STATUS.DENIED) {
      console.log('[CameraContext] Permission denied, not requesting');
      return null;
    }

    initializingRef.current = true;
    const callTimestamp = Date.now();
    console.log(`[CameraContext] ⚠️ getUserMedia CALLED at ${callTimestamp} - this should only happen ONCE per session`);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });

      // Generate a unique ID for this stream
      sessionStreamId = `stream_${callTimestamp}`;
      sessionStreamInitialized = true;
      
      console.log('[CameraContext] ✅ Stream started successfully', {
        streamId: sessionStreamId,
        trackCount: stream.getTracks().length
      });
      
      streamRef.current = stream;
      
      setPermissionStatus(PERMISSION_STATUS.GRANTED);
      localStorage.setItem(CAMERA_PERMISSION_KEY, PERMISSION_STATUS.GRANTED);
      setIsStreamReady(true);
      initializingRef.current = false;
      
      return stream;
    } catch (error) {
      console.error('[CameraContext] ❌ getUserMedia error:', error.name, error.message);
      initializingRef.current = false;
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setPermissionStatus(PERMISSION_STATUS.DENIED);
        localStorage.setItem(CAMERA_PERMISSION_KEY, PERMISSION_STATUS.DENIED);
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        // No camera device available - treat as denied for UI purposes
        console.log('[CameraContext] No camera device found - setting status to DENIED for fallback UI');
        setPermissionStatus(PERMISSION_STATUS.DENIED);
        localStorage.setItem(CAMERA_PERMISSION_KEY, PERMISSION_STATUS.DENIED);
      }
      
      return null;
    }
  }, [permissionStatus, hasActiveStream]);

  // Safely attach stream to a video element - iOS friendly
  const attachStreamToVideo = useCallback((videoElement) => {
    if (!videoElement) {
      console.log('[CameraContext] attachStreamToVideo: No video element provided');
      return false;
    }
    
    if (!hasActiveStream()) {
      console.log('[CameraContext] attachStreamToVideo: No active stream to attach');
      return false;
    }

    const stream = streamRef.current;
    
    // Check if already attached to avoid unnecessary reattachment
    if (videoElement.srcObject === stream) {
      console.log('[CameraContext] attachStreamToVideo: Stream already attached to this video element');
      return true;
    }

    console.log('[CameraContext] attachStreamToVideo: Attaching stream to video element', {
      sessionStreamId,
      videoCurrentSrc: videoElement.srcObject ? 'has source' : 'no source'
    });

    videoElement.srcObject = stream;
    return true;
  }, [hasActiveStream]);

  // Capture photo from a video element - NO stream restart
  const capturePhotoFromVideo = useCallback((videoElement) => {
    if (!videoElement || !canvasRef.current) {
      console.log('[CameraContext] Video or canvas not ready for capture');
      return null;
    }

    const canvas = canvasRef.current;

    // Set canvas size to match video
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;

    // Draw current frame to canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

    // Get image as base64
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    console.log('[CameraContext] Photo captured from video, size:', imageData.length);

    return imageData;
  }, []);

  // Show camera overlay
  const showCamera = useCallback(async () => {
    console.log('[CameraContext] showCamera called');
    setIsCameraVisible(true);
    
    // Initialize stream if not ready
    if (!hasActiveStream()) {
      await initializeStream();
    }
  }, [initializeStream, hasActiveStream]);

  // Hide camera overlay (does NOT stop the stream)
  const hideCamera = useCallback(() => {
    console.log('[CameraContext] hideCamera called - stream stays alive');
    setIsCameraVisible(false);
  }, []);

  // Stop stream completely (only call on explicit user action or logout)
  const stopStream = useCallback(() => {
    console.log('[CameraContext] stopStream called - stopping all tracks');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        console.log('[CameraContext] Stopping track:', track.kind, track.readyState);
        track.stop();
      });
      streamRef.current = null;
      sessionStreamInitialized = false;
      sessionStreamId = null;
      setIsStreamReady(false);
    }
  }, []);

  // Check permission on mount - but do NOT auto-initialize stream
  useEffect(() => {
    const checkPermission = async () => {
      if ('permissions' in navigator) {
        try {
          const result = await navigator.permissions.query({ name: 'camera' });
          console.log('[CameraContext] Permissions API result:', result.state);
          
          if (result.state === 'granted') {
            setPermissionStatus(PERMISSION_STATUS.GRANTED);
            localStorage.setItem(CAMERA_PERMISSION_KEY, PERMISSION_STATUS.GRANTED);
          } else if (result.state === 'denied') {
            setPermissionStatus(PERMISSION_STATUS.DENIED);
            localStorage.setItem(CAMERA_PERMISSION_KEY, PERMISSION_STATUS.DENIED);
          }

          // Listen for changes
          result.onchange = () => {
            console.log('[CameraContext] Permission changed to:', result.state);
            if (result.state === 'granted') {
              setPermissionStatus(PERMISSION_STATUS.GRANTED);
              localStorage.setItem(CAMERA_PERMISSION_KEY, PERMISSION_STATUS.GRANTED);
            } else if (result.state === 'denied') {
              setPermissionStatus(PERMISSION_STATUS.DENIED);
              localStorage.setItem(CAMERA_PERMISSION_KEY, PERMISSION_STATUS.DENIED);
            }
          };
        } catch (e) {
          console.log('[CameraContext] Permissions API not available for camera');
        }
      }
    };

    checkPermission();

    // Cleanup on unmount (app close) - but only stop tracks, don't prevent reuse
    return () => {
      console.log('[CameraContext] Provider unmounting');
      // Only stop if we have tracks - don't reset session flags here
      // as this might be a React StrictMode double-mount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          console.log('[CameraContext] Cleanup: stopping track', track.kind);
          track.stop();
        });
      }
    };
  }, []);

  const value = {
    // State
    permissionStatus,
    isStreamReady,
    isCameraVisible,
    
    // Refs for direct access
    streamRef,
    canvasRef,
    
    // Methods
    initializeStream,
    attachStreamToVideo,
    capturePhotoFromVideo,
    hasActiveStream,
    showCamera,
    hideCamera,
    stopStream,
    
    // Constants
    PERMISSION_STATUS
  };

  return (
    <CameraContext.Provider value={value}>
      {children}
      {/* Hidden canvas for photo capture - always mounted */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </CameraContext.Provider>
  );
};

export default CameraContext;
